import { mapState } from 'vuex'
import { requiredIf, required } from 'vuelidate/lib/validators'
import LogList from '@/components/log/list/LogList.vue'
import AddressPicker from '@/components/common/addresspicker/AddressPicker.vue'
import D365AddressPicker from '@/components/common/d365addresspicker/D365AddressPicker.vue'
import BankPicker from '@/components/bank/picker/BankPicker.vue'
import ClientContractEdit from '@/components/client/contract/edit/ClientContractEdit.vue'
import ClientComment from '@/components/client/comment/ClientComment.vue'
import ClientProductService from '@/components/client/productservice/ClientProductService.vue'
import ClientUserList from '@/components/client/user/list/ClientUserList.vue'
import MaterialRatesList from '@/components/material/rateslist/MaterialRatesList.vue';
import ReportsList from '@/components/reports/list/ReportsList.vue';
import ClientBills from '@/components/client/bills/list/ClientBills.vue';
import ClientMaterialGroupSettings from '@/components/client/materialgroupsettings/ClientMaterialGroupSettings.vue'
import Fileupload from '@/components/common/fileupload/Fileupload.vue';
import AuditList from '@/components/audit/list/AuditList.vue';
import SummaryPlacedSimple from '@/components/reports/summaryplacedsimple/SummaryPlacedSimple.vue'
import MessageBox from '@/components/common/messagebox/MessageBox.vue';
import ClientPicker from '@/components/client/picker/ClientPicker.vue';
import AdvancedSelect from '@/components/common/advancedselect/AdvancedSelect.vue'
import CompanyDataSearch from '@/components/common/companydatasearch/CompanyDataSearch.vue'
import AdvancedSelectWithPath from '../../common/advancedselectwithpath/AdvancedSelectWithPath.vue'

const defaultBankAccount = {
  id: null,
  accountNumber: '',
  bank: {
    swift: null
  },
  currency_id: null
};

const defaultClient = {
  api_id: null,
  balance: null,
  name: '',
  category: null,
  manager: null,
  axaptCode: '',
  registryNumber: '',
  pvnRegistryNumber: '',
  postAddress: {},
  legalAddress: {},
  receiptReceiverEmails: '',
  primary_filler_user: null,
  product_categories: [],
  bank_accounts: [defaultBankAccount],
  users: [],
  parent_client_id: null,
  isResident: null,
  classification_group_id: null,
  customer_group_id: null,
  country_id: null,
  currency_id: null,
  tax_group_id: null,
  customer_payment_method_id: null,
  intercompany_id: null,
  payment_term_id: null,
  autoSettle: null
}

export default {
  name: 'ClientEdit',
  data () {
    return {
      clientApiId: null,
      currentDuplicateRequest: null,
      clientExists: false,
      loading: true,
      loadingDuplicateRequest: false,
      updatingFiles: false,
      client: null,
      errors: null,
      filterValues: {
        comments: {
          comment_type: null
        },
        log: {
          type: null,
          date: null
        }
      },
      paging: {
        bills: { limit: 10, page: 1, total: 0, pages: 0, path: 'bills' },
        comments: { limit: 10, page: 1, total: 0, pages: 0, path: 'comments' },
        contracts: { limit: 10, page: 1, total: 0, pages: 0, path: 'contracts' },
        reports: { limit: 10, page: 1, total: 0, pages: 0, path: 'reports' },
        users: { limit: 10, page: 1, total: 0, pages: 0, path: 'users' }
      },
      tabIndex: 0
    }
  },
  components: {
    ClientPicker,
    LogList,
    AddressPicker,
    BankPicker,
    ClientComment,
    ClientUserList,
    ClientContractEdit,
    ClientProductService,
    MaterialRatesList,
    ReportsList,
    ClientBills,
    ClientMaterialGroupSettings,
    Fileupload,
    AuditList,
    SummaryPlacedSimple,
    MessageBox,
    D365AddressPicker,
    AdvancedSelect,
    CompanyDataSearch,
    AdvancedSelectWithPath
  },
  validations () {
    const SEMICOLON_EMAIL_LIST = /^([^\s@;,]+@[^\s@;,]+\.[^\s@;,]+)(;[^\s@;,]+@[^\s@;,]+\.[^\s@;,]+)*$/;

    const rules = {
      client: {
        files: {
          $each: {
            file: {
              required: requiredIf(function (el) {
                return (el && !el.filename);
              })
            }
          }
        },
        receiptReceiverEmails: {
          semicolonEmails: (value) => {
            if (!value) return true;
            return SEMICOLON_EMAIL_LIST.test(value);
          }
        }
      }
    };

    Object.entries(this.formFields)
      .filter(([key, field]) => field.required === true)
      .forEach(([key, field]) => {
        if (key === 'legalAddress') {
          rules.client[key] = {
            text: { required }
          };
        } else if (key === 'registryNumber') {
          rules.client[key] = {
            required,
            unique: () => !this.clientExists
          };
        } else {
          rules.client[key] = { required };
        }
      });

    return rules;
  },
  computed: {
    ...mapState(['config']),
    formFields () {
      const fields = {
        d365Code: { type: 'text', class: 'col-sm-3 font-weight-bold', disabled: true },
        d365UpdateStatus: { type: 'd365UpdateStatus', class: 'col-sm-9 d-flex align-items-center justify-content-end', disabled: true },
        separator: { type: 'separator', class: 'col-sm-12', disabled: true },
        name: { type: 'text', displayClient: true, required: true, disabled: !this.canEdit },
        classification_group_id: { type: 'select', options: this.clientClassificationGroupOptions, label: 'client.classification_group_id', textKey: 'code', class: 'col-sm-3', required: false, disabled: !this.isAdmin },
        axaptCode: { type: 'text', class: 'col-sm-3', disabled: !this.isAdmin },
        registryNumber: { type: 'text', displayClient: true, class: 'col-sm-3', required: true, disabled: !this.canEdit },
        pvnRegistryNumber: { type: 'text', displayClient: true, class: 'col-sm-3', disabled: !this.canEdit },
        customer_group_id: { type: 'select', options: this.customerGroupOptions, label: 'client.customer_group_id', class: 'col-sm-3', required: true, disabled: !this.canEdit },
        payment_term_id: { type: 'select', options: this.paymentTermOptions, label: 'client.payment_term_id', class: 'col-sm-3', disabled: !this.canEdit },
        country_id: { type: 'select', options: this.countryOptions, label: 'client.country_id', class: 'col-sm-3', required: true, disabled: !this.canEdit },
        currency_id: { type: 'select', options: this.currencyOptions, label: 'client.currency_id', class: 'col-sm-3', required: true, disabled: !this.canEdit },
        tax_group_id: { type: 'select', options: this.taxGroupOptions, label: 'client.tax_group_id', class: 'col-sm-6', required: true, disabled: !this.canEdit },
        legalAddress: { type: 'address', displayClient: true, required: true, disabled: !this.canEdit },
        customer_payment_method_id: { type: 'select', options: this.customerPaymentMethodOptions, label: 'client.customer_payment_method_id', class: 'col-sm-3', required: true, disabled: !this.canEdit },
        locale_id: { type: 'select', options: this.localeOptions, class: 'col-sm-3', required: true, disabled: !this.canEdit },
        isResident: { type: 'radio', options: this.yesNoOptions, required: true, disabled: !this.canEdit },
        autoSettle: { type: 'radio', options: this.yesNoOptions, required: true, disabled: !this.canEdit },
        bank_accounts: { type: 'bank_accounts', class: 'col-sm-12', disabled: false },
        manager: { type: 'select', options: this.managerOptions, label: 'client.manager', key: 'api_id', textKey: 'text', class: 'col-sm-6', disabled: !this.isAdmin },
        receiptReceiverEmails: { type: 'text', displayClient: true, disabled: !this.canEdit },
        postAddress: { type: 'address', displayClient: true, disabled: !this.canEdit },
        primary_filler_user: { type: 'select', options: this.clientUserOptions, label: 'client.primary_filler_user', key: 'api_id', textKey: 'text', displayClient: true, required: !this.isAdmin, disabled: !this.canEdit },
        parent_client_id: { type: 'parentclient', displayClient: false, disabled: !this.canEdit },
        registryNumberEeireg: { type: 'text', displayClient: false, disabled: !this.canEdit }
      };
      if (this.isAdmin) {
        return fields;
      } else {
        const usedFields = {};
        Object.keys(fields).forEach(key => {
          if (fields[key].displayClient) {
            usedFields[key] = fields[key];
          }
        });
        return usedFields;
      }
    },
    yesNoOptions () {
      return this.BasicHelper.yesNoOptions();
    },
    paymentTermOptions () {
      return this.$store.getters.getConfigOptions('payment_terms', this.$i18n.locale);
    },
    countryOptions () {
      const countryOptions = this.$store.getters.getConfigOptions('countries', this.$i18n.locale);

      countryOptions.forEach((country) => {
        country.text = country.text + ' (' + country.code + ')';
      });

      return countryOptions;
    },
    localeOptions () {
      return this.$store
        .getters
        .getConfigOptions('locales', this.$i18n.locale)
        .filter(option => option.code !== 'ru');
    },
    currencyOptions () {
      return this.$store.getters.getConfigOptions('currencies', this.$i18n.locale);
    },
    customerGroupOptions () {
      return this.$store.getters.getConfigOptions('customer_groups', this.$i18n.locale);
    },
    customerPaymentMethodOptions () {
      return this.$store.getters.getConfigOptions('customer_payment_methods', this.$i18n.locale);
    },
    clientClassificationGroupOptions () {
      return this.$store.getters.getConfigOptions('customer_priority_classification_groups', this.$i18n.locale);
    },
    taxGroupOptions () {
      return this.$store.getters.getConfigOptions('tax_groups', this.$i18n.locale);
    },
    intercompanyOptions () {
      return this.$store.getters.getConfigOptions('intercompanies', this.$i18n.locale);
    },
    clientTabs () {
      const tabs = ['#reports', '#bills', '#contracts', '#users'];
      if (this.isAdmin) {
        tabs.push('#comments');
        tabs.push('#material-rates');
        tabs.push('#audit');
        tabs.push('#log');
      }
      return tabs;
    },
    clientUserOptions () {
      let options = [];
      if (this.client) {
        options = this.BasicHelper.cloneObject(this.client.users);
        options.forEach(el => {
          el.text = el.name + ' (' + el.email + ')';
        });
        if (this.isAdmin) {
          options.unshift({ api_id: null, text: '' });
        }
      }
      return options;
    },
    commentTypeOptions () {
      const options = this.$store.getters.getConfigOptions('comment_types', this.$i18n.locale);
      options.unshift({ text: '', code: null });
      return options;
    },
    managerOptions () {
      const options = this.$store.getters.getConfigOptions('managers', null, null, null, false, false, false, ['api_id']);
      options.unshift({ text: '', id: null });
      return options;
    },
    syncParams () {
      if (this.isAdmin) {
        return this.client.sync_params ?? [
          'd365Code',
          'name',
          'registryNumber',
          'pvnRegistryNumber',
          'legalAddress',
          'classification_group_id',
          'axaptCode',
          'customer_group_id',
          'locale_id',
          'payment_term_id',
          'country_id',
          'currency_id',
          'tax_group_id',
          'customer_payment_method_id',
          'isResident',
          'autoSettle',
          'intercompany_id'
        ];
      }
      return [];
    },
    tableHeaders () {
      const headerConf = {
        contracts: [
          { key: 'material_group_id', label: this.$t('material-group.title'), format: 'formatFromConfig', configKey: 'material_groups', configParam: 'name' },
          { key: 'full_contract_number', label: this.$t('contract.contractNumber') },
          { key: 'dateFrom', label: this.$t('contract.dateFrom'), type: 'date' },
          { key: 'from_date_period_id', label: this.$t('general.filters.date-period-from'), format: 'formatFunc', funcs: [this.formatContractPeriod], classFuncs: [] },
          { key: 'to_date_period_id', label: this.$t('general.filters.date-period-to'), format: 'formatFunc', funcs: [this.formatContractPeriod], classFuncs: [] },
          { key: 'isCanceled', label: this.$t('contract.isCanceled'), format: 'formatYesNo' },
          { key: 'advanceBill', label: this.$t('contract.advanceBill'), format: 'formatYesNo' },
          { key: 'penalty', label: this.$t('contract.penalty'), format: 'formatPercent' },
          { key: 'dueInDays', label: this.$t('contract.dueInDays') },
          { key: 'agreements.length', label: this.$t('contractAgreement.itemCount') },
          { key: 'poNumber', label: this.$t('contract.poNumber') },
          { key: 'actions', label: '', actions: [{ label: this.$t('menu.contract.view'), emit: 'editContract', icon: 'edit', bicon: 'pencil-fill' }] }
        ],
        comments: [
          { key: 'comment_type_id', label: this.$t('comment.type'), format: 'formatFromConfig', configKey: 'comment_types', configParam: 'name', filterKey: 'comment_type', class: 'col-width-large' },
          { key: 'updated_at', label: this.$t('general.updated_at'), type: 'datetime', class: 'col-width-medium' },
          { key: 'user.name', label: this.$t('user.name'), format: 'formatOtherValue', otherKey: 'authorName', filterKey: 'user', class: 'col-width-xl' },
          { key: 'comment', label: this.$t('comment.comment'), class: 'pre-formatted' },
          { key: 'actions', label: '', actions: [{ label: this.$t('menu.comment.edit'), emit: 'editComment', icon: 'edit', bicon: 'pencil-fill' }] }
        ],
        users: [
          { key: 'name', label: this.$t('user.name') },
          { key: 'phones', label: this.$t('user.phone'), format: 'formatPhones' },
          { key: 'email', label: this.$t('user.email'), format: 'formatEmail' },
          { key: 'jobPosition.' + this.$i18n.locale, label: this.$t('user.jobPosition') },
          { key: 'pivot.canFillReports', label: this.$t('user.pivot.canFillReports'), format: 'formatYesNo' },
          { key: 'pivot.isContactPerson', label: this.$t('user.pivot.isContactPerson'), format: 'formatYesNo' },
          { key: 'pivot.isResponsiblePerson', label: this.$t('user.pivot.isResponsiblePerson'), format: 'formatYesNo' },
          { key: 'pivot.receiveBill', label: this.$t('user.pivot.receiveBill'), format: 'formatYesNo' },
          { key: 'pivot.receiveNotifications', label: this.$t('user.pivot.receiveNotifications'), format: 'formatYesNo' },
          { key: 'actions', label: '', actions: [{ label: this.$t('menu.user.edit'), emit: 'editUser', icon: 'edit', bicon: 'pencil-fill' }] }
        ]
      };
      if (this.isAdmin) {
        headerConf.contracts.find(el => el.key == 'actions').actions.push(
          { label: this.$t('menu.contract.delete'), emit: 'deleteContract', icon: 'delete', bicon: 'trash' }
        )
      }
      return headerConf;
    },
    tableFilterConfig () {
      const filters = { };
      filters.comments = {
        comment_type: { type: 'select', label: '', options: this.commentTypeOptions, colOnlyFilter: true },
        user: { type: 'userpicker', label: '', colOnlyFilter: true }
      };
      filters.log = {
        type: { type: 'select', label: this.$t('log.filters.type'), options: this.logRecordTypeOptions, colOnlyFilter: true },
        date: { type: 'date', label: this.$t('general.filters.datefrom'), colOnlyFilter: true, short: true },
        user: { type: 'userpicker', label: this.$t('log.user'), colOnlyFilter: true }
      };
      return filters;
    },
    tableExpandConfig () {
      return { contracts: { component: 'ClientContractListExpand', info: { clientApiId: this.clientApiId, itemname: 'contract', contracts: this.client.contracts } } };
    },
    logRecordTypeOptions () {
      let options = this.$store.getters.getConfigOptions('log_record_types', this.$i18n.locale);
      options = options.filter(el => (el.code.indexOf('CLIENT-') === 0));
      options.unshift({ code: null, text: '' });
      return options;
    },
    viewAll () {
      return this.ApiRequest.userHasPermission('view-clients');
    },
    canEdit () {
      return this.ApiRequest.userHasPermission('edit-clients', this.client.api_id, []);
    },
    isExisting () {
      if (this.client.api_id) {
        return true;
      }
      return false;
    },
    isAdmin () {
      return this.ApiRequest.userHasPermission('edit-clients');
    },
    isD365BillingEnabled () {
      return !!this.BasicHelper.getObjectValueByString(this.config, 'plugins.d365_api.enabled');
    },
    clientBalance () {
      if (this.isD365BillingEnabled) {
        return parseFloat(this.client.balanceAdvanceBill || 0) + parseFloat(this.client.d365Balance || 0);
      }
      return parseFloat(this.client.balance || 0) + parseFloat(this.client.balanceAdvanceBill || 0) + parseFloat(this.client.balanceOpenOrders || 0);
    }
  },
  methods: {
    loadClientData () {
      if (this.clientApiId === 'NEW') {
        this.client = this.BasicHelper.cloneObject(defaultClient);

        const iso = this.customerPaymentMethodOptions.find(
          el => el.code == 'ISO'
        );
        this.client.customer_payment_method_id = iso ? iso.value : null;

        const intercompany = this.intercompanyOptions.find(
          el => el.code == 'AA'
        );
        this.client.intercompany_id = intercompany ? intercompany.value : null;

        const paymentTerm = this.paymentTermOptions.find(
          el => el.code == '15D'
        );
        this.client.payment_term_id = paymentTerm ? paymentTerm.value : null;

        const manager = this.managerOptions.find(
          el => el.api_id == this.$session.get('uuid')
        );
        this.client.manager = manager ? manager.api_id : null;

        this.loading = false;
      } else {
        const self = this;
        this.ApiRequest.request('client/single/' + this.clientApiId, this.ApiRequest.REQUEST_GET, [], function (response) {
          if (response.data) {
            const clientData = response.data;
            if (clientData.manager_id) {
              clientData.manager = self.$store.getters.getParamFromConfig('managers', clientData.manager_id, 'api_id');
            } else {
              clientData.manager = null;
            }
            const fillUser = clientData.users.find(el => el.id === clientData.primary_filler_user_id);
            if (fillUser) {
              clientData.primary_filler_user = fillUser.api_id
            } else {
              clientData.primary_filler_user = null;
            }
            if (clientData.bank_accounts.length === 0) {
              clientData.bank_accounts.push(self.BasicHelper.cloneObject(defaultBankAccount));
            }
            if (!clientData.bank_accounts[0].bank) {
              clientData.bank_accounts[0].bank = self.BasicHelper.cloneObject(defaultBankAccount.bank);
            }
            self.client = clientData;
            for (const [key, value] of Object.entries(response.data.paging)) {
              self.paging[key] = value;
            }
          } else {
            if (response.messageCodes.includes('access-denied')) {
              self.$router.push('/client');
            }
            self.errors = response.messageCodes;
          }
          self.loading = false;
        });
      }
    },
    getTableData (pagingKey) {
      this.changePage(this.paging[pagingKey].page, pagingKey);
    },
    changePage (pageNr, pagingKey) {
      this.paging[pagingKey].page = pageNr;
      this.paging[pagingKey].loading = true;
      this.$forceUpdate();
      let path = 'client/single/' + this.clientApiId + '/' + this.paging[pagingKey].path + '/?perPage=' + this.paging[pagingKey].limit + '&page=' + pageNr;
      if (this.$refs['tableview-' + pagingKey]) {
        path = this.ApiRequest.addGetParamsToPath(path, this.$refs['tableview-' + pagingKey].getFilterValues(), true);
      }
      const self = this;
      this.ApiRequest.request(path, this.ApiRequest.REQUEST_GET, [], function (response) {
        if (response.data) {
          self.client[pagingKey] = response.data;
          self.paging[pagingKey].limit = response.paging.limit;
          self.paging[pagingKey].total = response.paging.total;
          self.paging[pagingKey].page = response.paging.page;
          self.paging[pagingKey].pages = response.paging.pages;
        }
        self.paging[pagingKey].loading = false;
        self.$forceUpdate();
      })
    },
    saveGeneral () {
      this.$v.$touch();
      if (!this.$v.$invalid && !this.loading) {
        this.loading = true;
        const reqData = { client: {}, bank_account: [] };
        for (const [key, config] of Object.entries(this.formFields)) {
          if (!config.disabled) {
            if (this.client[key] !== null && config.key && this.client[key][config.key]) {
              reqData.client[key] = this.client[key][config.key];
            } else {
              reqData.client[key] = this.client[key];
            }
          }
        }
        if (this.clientApiId === 'NEW') {
          reqData.client.intercompany_id = this.client.intercompany_id;
        }
        this.client.bank_accounts.forEach(function (item) {
          reqData.bank_account.push({ id: item.id, accountNumber: item.accountNumber, bank: (item.bank ? item.bank.swift : null), currency_id: item.currency_id });
        });

        const self = this;
        this.ApiRequest.request('client/single/' + this.clientApiId, this.ApiRequest.REQUEST_POST, reqData, function (response) {
          if (response.error) {
            self.ApiRequest.displayErrorDialog(response, self, 'client');
          } else if (self.clientApiId === 'NEW') {
            self.$router.push('/client/' + response.data.client.api_id + '/edit');
          } else if (response.data.updatedCount > 0) {
            self.ApiRequest.displaySuccessDialog(self, 'client.saved', 'general.saved');
            if (self.$refs.logList) {
              self.$refs.logList.reload();
            }
          }
          self.loading = false;
        });
      }
    },
    setSelectedClavizPartnerData (data) {
      this.client.name = data.name_normalized;
      this.client.registryNumber = String(data.regcode ?? '');
      this.client.pvnRegistryNumber = data.vat_code;

      if (data.local_address) {
        this.client.legalAddress.text = data.local_address;
      } else if (data.address) {
        this.client.legalAddress.text = data.address;
      }

      if (data.address_id) {
        this.client.legalAddress.custom = false;
        this.client.legalAddress.id = data.address_id;
      } else {
        this.client.legalAddress.custom = true;
        this.client.legalAddress.id = null;
      }
    },
    savedClientContract () {
      this.changePage(this.paging.contracts.page, 'contracts');
      this.$refs.logList.reload();
    },
    changeAddress (key, address) {
      this.$set(this.client, key, {
        ...this.client[key],
        text: address.text,
        id: address.id,
        custom: address.manualEntry
      });
    },
    editContract (contract) {
      this.$refs.clientContract.show(contract, this.clientApiId);
      this.$refs.clientContract.show(contract, this.clientApiId);
    },
    deleteContract (el, approved = 0) {
      if (approved) {
        const path = 'client/single/' + this.clientApiId + '/contracts/deleted';
        const self = this;
        this.ApiRequest.request(path, this.ApiRequest.REQUEST_POST, { id: el.id, deleted: 1 }, function (response) {
          if (response.error) {
            self.ApiRequest.displayErrorDialog(response, self);
            self.loading = false;
          } else {
            self.savedClientContract();
          }
        });
      } else {
        this.$refs.contractDeleteMsgBox.show(el, this.$t('menu.contract.deleteTitle'), this.$t('menu.contract.deleteMessage', el))
      }
    },
    formatContractPeriod (value, data) {
      if (!value) {
        return '';
      }
      const materialGroupCode = this.$store.getters.getParamFromConfig('material_groups', data.item.material_group_id, 'code');
      if (materialGroupCode === 'AUTH') {
        return (value + '').substr(0, 4);
      }
      const periodData = this.BasicHelper.datePeriodCodeToData(value);
      return periodData ? this.$tc('general.datePeriod.short', periodData.period, periodData) : '';
    },
    checkForDuplicates () {
      if (this.currentDuplicateRequest) {
        this.currentDuplicateRequest.cancel('changed search query');
      }

      this.loadingDuplicateRequest = true;
      const path = this.ApiRequest.addGetParamsToPath('client/check-duplicates', { id: this.client.id, registryNumber: this.client.registryNumber });

      this.currentDuplicateRequest = this.ApiRequest.request(path, this.ApiRequest.REQUEST_GET, {}, (response) => {
        if (response.data) {
          this.clientExists = response.data.exists;
        }

        this.loadingDuplicateRequest = false;
      });
    },
    editComment (comment) {
      this.$refs.clientComment.show(comment, this.clientApiId);
    },
    editBlocked (isBlocked, approved = false) {
      if (approved) {
        this.loading = true;
        const postData = { isBlocked: isBlocked, fullunlock: false };
        if (isBlocked === 'fullunblock') {
          postData.isBlocked = 0;
          postData.fullunlock = true;
        }
        const self = this;
        this.ApiRequest.request('client/single/' + this.clientApiId + '/set-blocked', this.ApiRequest.REQUEST_POST, { client: postData }, function (response) {
          if (response.error) {
            self.ApiRequest.displayErrorDialog(response, self);
          }
          if (response.data) {
            self.client.isBlocked = response.data.isBlocked;
            self.client.isAxBlocked = response.data.isAxBlocked;
          }
          self.$refs.logList.reload();
          self.loading = false;
        });
      } else {
        this.$refs.blockUnblockMsgBx.show(isBlocked, this.$t('client.blocktoggle.' + isBlocked + '.title'), this.$t('client.blocktoggle.' + isBlocked + '.message', { name: this.client.name }))
      }
    },
    commentSaved (data) {
      this.changePage(this.paging.comments.page, 'comments');
      if (Object.prototype.hasOwnProperty.call(data, 'isBlocked')) {
        this.$refs.logList.reload();
        this.client.isBlocked = data.isBlocked;
      }
    },
    discountsUpdated (data) {
      this.client.material_group_settings = data;
      this.$refs.logList.reload();
    },
    setComponentData () {
      this.client = null;
      this.loading = true;
      if (this.$route.path === '/myclient') {
        this.clientApiId = this.BasicHelper.getUserClientApiId();
      } else {
        this.clientApiId = this.$route.params.clientApiId;
      }
      this.loadClientData()
    },
    downloadfile (key) {
      this.client.files[key].loading = true;
      this.$refs.fileupload.forceUpdate(key);
      const self = this;
      this.ApiRequest.request('client/files/download/client/' + this.client.files[key].id, this.ApiRequest.REQUEST_DOWNLOAD, [], function () {
        self.client.files[key].loading = false;
        self.$refs.fileupload.forceUpdate(key);
      });
    },
    updateClientFiles () {
      this.$v.$touch();
      if (!this.$v.client.files.$invalid && !this.loading) {
        this.loading = true;
        this.updatingFiles = true;
        this.$refs.fileupload.$forceUpdate();
        const self = this;
        const reqData = this.ApiRequest.createFormData(this.client, [], 'client', this);
        this.ApiRequest.request('client/single/' + this.clientApiId + '/files', this.ApiRequest.REQUEST_FILEUPLOAD, reqData, function (response) {
          if (response.error) {
            self.ApiRequest.displayErrorDialog(response, self, 'client');
          } else {
            self.client.files = response.data;
            self.$refs.logList.reload();
          }
          self.updatingFiles = false;
          self.loading = false;
        });
      }
    },
    tabTableItemsUpdated (data) {
      if (data) {
        if (Object.prototype.hasOwnProperty.call(data, 'contracts')) {
          this.client.contracts = data.contracts;
          this.$refs.logList.reload();
        }
      } else {
        this.getTableData('contracts')
      }
    },
    updatedParentclient (val) {
      this.client.parent_client_id = (val ? val.id : null);
    },
    setActiveTabFromHash () {
      const tabIndex = this.clientTabs.findIndex(tab => tab === this.$route.hash);
      this.tabIndex = tabIndex >= 0 ? tabIndex : 0;
    }
  },
  mounted () {
    this.setComponentData();
    this.setActiveTabFromHash();
  },
  watch: {
    '$route' () {
      this.setComponentData();
      this.setActiveTabFromHash();
    }
  }
}
